// Removed local font.css import; fonts now injected via MUI CssBaseline fontFaces from @repo/ui.

import { type INavigationClient } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import NiceModal from "@ebay/nice-modal-react";
import { AppInsightsContext, ReactPlugin } from "@microsoft/applicationinsights-react-js";
import { ApplicationInsights, type ITelemetryItem } from "@microsoft/applicationinsights-web";
import CssBaseline from "@mui/material/CssBaseline/CssBaseline";
import ThemeProvider from "@mui/material/styles/ThemeProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { globalHistory, useLocation } from "@reach/router";
import { msalInstance, msalReady } from "@repo/api";
import { EnvironmentHelper, readEnv } from "@repo/helpers";
import { store } from "@repo/logic";
import { CheckContactInfoLayout, DefaultLayout, LoginLayout } from "@repo/ui";
import csLocale from "date-fns/locale/cs";
import type { GatsbyBrowser } from "gatsby";
import { navigate } from "gatsby";
import { type FC, type PropsWithChildren, useEffect, useState } from "react";
import { Provider } from "react-redux";
import { NavigationTabsProvider } from "./src/components/BottomNavigation/context";
import { SmartActionProvider } from "./src/components/Contract/SmartAction/context";
import { DrawerProvider } from "./src/components/Drawer/context";
import { cookies } from "./src/components/Drawer/CookieDrawer/CookieIds";
import { ErrorBoundaryWithAsyncContext } from "./src/components/ErrorBoundary";
import { DefaultErrorUIWithTheme } from "./src/components/ErrorBoundary/DefaultErrorUi";
import { globalFormikYupConfig } from "./src/components/Form/rules";
import Layout, { LayoutProvider } from "./src/components/Layout";
import { ProfileLayout } from "./src/components/Profile/Layout";
import { ReactQueryDevtoolsGate } from "./src/components/ReactQueryDevtoolsGate/ReactQueryDevtoolsGate";
import { StepperManager } from "./src/components/StepperManager/StepperManager";
import { CookieContextProvider } from "./src/context/CookieContext";
import { DataConfigContextProvider } from "./src/context/DataConfigContext";
import { PrimaryAPIClientProvider } from "./src/context/PrimaryAPIClient";
import ReactQueryClientProvider from "./src/context/ReactQueryContext";
import { SPErrorContextProvider } from "./src/context/SPErrorContext";
import theme from "./src/gatsby-theme-material-ui-top-layout/theme";
import { ContactInfoCheck } from "./src/images/ContactInfoCheck";
import { LoginImage } from "./src/images/LoginImage";
import { LogoutImage } from "./src/images/LogoutImage";
import { getAllowedCookiesFromLocalStorage } from "./src/utils";

/**
 * This is an example for overriding the default function MSAL uses to navigate to other urls in your webpage
 */
class CustomNavigationClient implements INavigationClient {
    /**
     * Navigates to other pages within the same web application
     * You can use the useRouter hook provided by next.js to take advantage of client-side routing
     * @param url
     * @param options
     */
    async navigateInternal(url: string, options: { noHistory?: boolean } = {}): Promise<boolean> {
        const relativePath = url.replace(window.location.origin, "");
        if (options.noHistory) {
            await navigate(relativePath, { replace: true });
        } else {
            await navigate(relativePath);
        }

        return false;
    }

    /**
     * Navigates to other pages outside the web application (external links)
     * @param url
     * @param options
     */
    async navigateExternal(url: string, options: { noHistory?: boolean } = {}): Promise<boolean> {
        if (options.noHistory) {
            window.location.replace(url);
        } else {
            window.location.assign(url);
        }

        return true;
    }
}

const reactPlugin = new ReactPlugin();

globalFormikYupConfig();

/**
 * Wrapper component that ensures MSAL is initialized before rendering children
 */
const MsalInitializer: FC<PropsWithChildren> = ({ children }) => {
    const [isInitialized, setIsInitialized] = useState(false);

    useEffect(() => {
        const initializeMsal = async () => {
            try {
                // Wait for MSAL initialization from @repo/api
                await msalReady;
                const navigationClient = new CustomNavigationClient();
                msalInstance.setNavigationClient(navigationClient);
                setIsInitialized(true);
            } catch (error) {
                console.error("MSAL initialization failed:", error);
                // Set initialized anyway to avoid infinite loading
                setIsInitialized(true);
            }
        };

        initializeMsal();
    }, []);

    if (!isInitialized) {
        return null;
    }

    return <>{children}</>;
};

export const wrapRootElement: GatsbyBrowser["wrapRootElement"] = ({ element }) => {
    const allowedCookies = getAllowedCookiesFromLocalStorage();

    const allowAppInsightCookies = allowedCookies.includes(cookies.appInsights);

    // Only initialize AppInsights during browser runtime with valid config
    const appInsightsKey = readEnv("GATSBY_APP_INSIGHTS_INST_KEY");
    const appInsightsConnectionString = readEnv("GATSBY_APP_INSIGHTS_CONNECTION_STRING");

    const appInsights =
        EnvironmentHelper.isBrowser() && (appInsightsKey || appInsightsConnectionString)
            ? new ApplicationInsights({
                  config: {
                      connectionString: appInsightsConnectionString,
                      instrumentationKey: appInsightsKey,
                      isCookieUseDisabled: !allowAppInsightCookies,
                      disableCookiesUsage: !allowAppInsightCookies,
                      // Reduce telemetry request frequency
                      maxBatchInterval: 30000, // Send batches every 30s instead of default 15s
                      maxBatchSizeInBytes: 100000, // Larger batches = fewer requests
                      disableAjaxTracking: false,
                      maxAjaxCallsPerView: 20, // Limit tracked AJAX calls per page (default 500)
                      disableFetchTracking: false,
                      enableRequestHeaderTracking: false,
                      enableResponseHeaderTracking: false,
                      samplingPercentage: 50, // Only send 50% of telemetry events
                      isBeaconApiDisabled: false, // Use navigator.sendBeacon for non-blocking sends
                      onunloadDisableBeacon: false, // Use beacon also on page unload
                      extensions: [reactPlugin],
                      extensionConfig: {
                          [reactPlugin.identifier]: { history: globalHistory },
                          AppInsightsCfgSyncPlugin: {
                              cfgUrl: "",
                          },
                      },
                  },
              })
            : null;
    if (appInsights && EnvironmentHelper.isBrowser()) {
        appInsights.addTelemetryInitializer((item: ITelemetryItem) => {
            if (item.baseType === "PageviewData" || item.baseType === "PageviewPerformanceData") {
                if (item.baseData) {
                    item.baseData.name = document.title;
                }
            }

            // Drop "React Component Engaged Time" metrics – the withAITracking HOC
            // fires these on every re-render, flooding the ingestion endpoint and blocking the UI.
            if (
                item.baseType === "MetricData" &&
                item.baseData?.metrics?.[0]?.name === "React Component Engaged Time (seconds)"
            ) {
                return false; // Returning false drops the telemetry item
            }
        });
    }

    if (appInsights && readEnv("NODE_ENV") !== "development") {
        appInsights.loadAppInsights();
    }

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <MsalInitializer>
                <AppInsightsContext.Provider value={reactPlugin}>
                    <Provider store={store}>
                        <ErrorBoundaryWithAsyncContext fallbackUI={DefaultErrorUIWithTheme}>
                            <DataConfigContextProvider>
                                <MsalProvider instance={msalInstance}>
                                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={csLocale}>
                                        <PrimaryAPIClientProvider>
                                            <CookieContextProvider>
                                                <ReactQueryClientProvider>
                                                    <SPErrorContextProvider>
                                                        <LayoutProvider>
                                                            <SmartActionProvider>
                                                                <NavigationTabsProvider>
                                                                    <NiceModal.Provider>
                                                                        <StepperManager />
                                                                        <ReactQueryDevtoolsGate />
                                                                        {element}
                                                                    </NiceModal.Provider>
                                                                </NavigationTabsProvider>
                                                            </SmartActionProvider>
                                                        </LayoutProvider>
                                                    </SPErrorContextProvider>
                                                </ReactQueryClientProvider>
                                            </CookieContextProvider>
                                        </PrimaryAPIClientProvider>
                                    </LocalizationProvider>
                                </MsalProvider>
                            </DataConfigContextProvider>
                        </ErrorBoundaryWithAsyncContext>
                    </Provider>
                </AppInsightsContext.Provider>
            </MsalInitializer>
        </ThemeProvider>
    );
};

const LayoutComponent: FC<PropsWithChildren<{ pageContext: any }>> = ({ pageContext, children }) => {
    if (pageContext?.additionalProps?.hasTransition) {
        return (
            <ProfileLayout isMobileCrossroad={Boolean(pageContext.additionalProps.isMobileCrossroad)}>
                {children}
            </ProfileLayout>
        );
    }
    if (pageContext?.additionalProps?.loginLayout) {
        const images = {
            "/bezpecne-odhlaseni/": <LogoutImage />,
        };
        type ImagePaths = keyof typeof images;
        const { pathname } = useLocation();
        const image = images[pathname as ImagePaths] || (
            <LoginImage opacity={pageContext.additionalProps.backgroundOpacity} />
        );

        return <LoginLayout image={image}>{children}</LoginLayout>;
    }
    if (pageContext?.additionalProps?.checkContactInfoLayout) {
        return <CheckContactInfoLayout ContactInfoCheck={ContactInfoCheck}>{children}</CheckContactInfoLayout>;
    }
    return <DefaultLayout>{children}</DefaultLayout>;
};

export const wrapPageElement: GatsbyBrowser["wrapPageElement"] = ({ element, props }) => {
    return (
        <DrawerProvider>
            <Layout
                {...props}
                appBar={props.pageContext.additionalProps?.appBar}
                bottomNavigation={props.pageContext.additionalProps?.hasBottomNavigation}
            >
                <ErrorBoundaryWithAsyncContext key={props.pageContext.page}>
                    <LayoutComponent pageContext={props.pageContext}>{element}</LayoutComponent>
                </ErrorBoundaryWithAsyncContext>
            </Layout>
        </DrawerProvider>
    );
};
